import { InteractiveZoneCircle } from "../types";

// How far from square a shape's box may be and still read as a round spot
const MAX_ASPECT_DIFFERENCE = 0.1;

/**
 * Circle attributes for a spot drawn as a round `<path>` or `<ellipse>`
 * instead of a `<circle>` (e.g. "Reservation Node" paths exported from
 * Figma), so the occupant avatar can be placed over it. Returns undefined
 * when the shape isn't round enough to stand in for the spot's dot.
 *
 * The shape's own transform is applied to the centre and radius instead of
 * being passed on, since some overlays (pulse indicator, room badge) only
 * read `cx`/`cy`.
 */
export function getAttributesCircleFromShape(
  shape: Pick<SVGGraphicsElement, "getAttribute" | "getBBox" | "transform">
): InteractiveZoneCircle | undefined {
  const { x, y, width, height } = shape.getBBox();
  if (!width || !height) return undefined;
  if (
    Math.abs(width - height) / Math.max(width, height) >
    MAX_ASPECT_DIFFERENCE
  ) {
    return undefined;
  }

  const localCx = x + width / 2;
  const localCy = y + height / 2;
  const localR = Math.min(width, height) / 2;
  const strokeWidth = Number.parseFloat(
    shape.getAttribute("stroke-width") ?? "0"
  );

  const matrix = shape.transform?.baseVal?.consolidate()?.matrix;
  if (!matrix) {
    return { strokeWidth, r: localR, cx: localCx, cy: localCy };
  }

  const { a, b, c, d, e, f } = matrix;
  const scale = Math.sqrt(Math.abs(a * d - b * c));
  return {
    strokeWidth: strokeWidth * scale,
    r: localR * scale,
    cx: a * localCx + c * localCy + e,
    cy: b * localCx + d * localCy + f
  };
}
